import Button from '@sats-group/fresh-fitness-ui-lib/react/button';
import Text from '@sats-group/fresh-fitness-ui-lib/react/text';
import Arrow from '@sats-group/icons/24/arrow-down';
import cn from 'classnames';
import React, { useRef } from 'react';

import Collapse from 'root/client/components/collapse/collapse';
import HiddenInput from 'root/client/components/hidden-input/hidden-input';
import TextField from 'root/client/components/text-field/text-field';
import useToggle from 'root/client/hooks/use-toggle';
import { replaceQueryParameters } from 'root/shared/replace-query-parameters';

import Swoop from '../swoop/swoop';
import CampaignCountdown from '../campaign-countdown/campaign-countdown';

import type { CampaignBanner as Props } from './campaign-banner.types';

const CampaignBanner: React.FunctionComponent<Props> = ({
  active,
  campaignEndDate,
  description,
  id,
  inactiveTitle,
  input,
  onInteraction,
  title,
  trigger,
}) => {
  const codeInput = useRef<HTMLInputElement>(null);
  const [isOpen, toggleOpen] = useToggle(false);

  const handleSubmit: React.SubmitEventHandler<HTMLFormElement> = event => {
    if (!onInteraction) {
      return;
    }

    event.preventDefault();

    if (!codeInput.current) {
      return;
    }

    const options = Object.assign(
      Object.fromEntries(input.data.map(entry => [entry.name, entry.value])),
      { [input.code.name]: codeInput.current.value },
    );

    onInteraction({
      data: replaceQueryParameters(input.api, options),
      gui: replaceQueryParameters(input.action, options),
    });
  };

  const campaignTitle = active?.discount.title || title;
  const campaignDescription = active?.discount.texts.length
    ? active.discount.texts[active.discount.texts.length - 1]
    : description;

  return (
    <div
      className={cn('campaign-banner', {
        'campaign-banner--active': active,
        'campaign-banner--inactive': !active,
      })}
    >
      {active ? <Swoop className="campaign-banner__swoop" /> : null}
      <button
        aria-controls={id}
        aria-expanded={isOpen}
        className={cn('campaign-banner__trigger', {
          'campaign-banner__trigger--inactive': !active,
        })}
        onClick={toggleOpen}
        type="button"
      >
        <span className="campaign-banner__information">
          {active ? (
            <>
              <Text
                size={Text.sizes.header3}
                theme={Text.themes.emphasis}
                variant={Text.variants.hero}
              >
                {campaignTitle}
              </Text>
              <Text size={Text.sizes.small}>{campaignDescription}</Text>
            </>
          ) : (
            <Text>{inactiveTitle}</Text>
          )}
        </span>
        <span className="campaign-banner__action">
          {active && campaignEndDate ? (
            <CampaignCountdown dateString={campaignEndDate} />
          ) : null}
          <span
            className={cn('campaign-banner__arrow', {
              'campaign-banner__arrow--open': isOpen,
            })}
          >
            <Arrow />
          </span>
        </span>
      </button>
      <Collapse isOpen={isOpen}>
        <div className="campaign-banner__content" id={id}>
          <div className="campaign-banner__content-header">
            <Text theme={Text.themes.emphasis}>{trigger}</Text>
            <Text size={Text.sizes.small}>{description}</Text>
          </div>
          <form
            action={input.action}
            className="campaign-banner__form"
            method="get"
            onSubmit={handleSubmit}
          >
            {input.data.map(entry => (
              <HiddenInput {...entry} key={`${entry.name}-${entry.value}`} />
            ))}
            <TextField {...input.code} ref={codeInput} required wide />
            <div className="campaign-banner__submit">
              <Button
                {...input.trigger}
                size={Button.sizes.basic}
                theme={
                  active ? Button.themes.primaryWhite : Button.themes.primary
                }
                type="submit"
                wide
              />
            </div>
          </form>
        </div>
      </Collapse>
    </div>
  );
};

export default CampaignBanner;
